/**
 * Ranking Engine
 * ──────────────
 * Derives Top-N, trending, and recently-used views from the combination set and
 * the immutable usage-event log. All pure; recomputed on demand (cheap because
 * combinations carry pre-aggregated counters).
 */

import type {
  Combination,
  CombinationAnalytics,
  TrendDirection,
  TrendingWindow,
  UsageEvent,
} from "@/types";
import { windowToDays, withinWindow } from "@/lib/utils/date";

/** Top-N combinations by aggregate usage count (ties broken by recency). */
export function topByUsage(combinations: Combination[], n: number): Combination[] {
  return [...combinations]
    .sort(
      (a, b) =>
        b.usageCount - a.usageCount ||
        new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime(),
    )
    .slice(0, n);
}

/** Most-recently executed combinations, newest first. */
export function recentlyUsed(combinations: Combination[], n: number): Combination[] {
  return [...combinations]
    .sort(
      (a, b) => new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime(),
    )
    .slice(0, n);
}

/** Count executions of a combination inside a trending window. */
export function windowCount(
  events: UsageEvent[],
  combinationId: string,
  window: TrendingWindow,
): number {
  return events.filter(
    (e) => e.combinationId === combinationId && withinWindow(e.timestamp, window),
  ).length;
}

/**
 * Trend direction compares executions in the most recent window-length against
 * the immediately preceding window of equal length. Rising/falling/stable.
 */
export function trendDirection(
  events: UsageEvent[],
  combinationId: string,
  window: TrendingWindow,
): TrendDirection {
  const days = windowToDays(window);
  const now = Date.now();
  const dayMs = 86_400_000;
  const currentStart = now - days * dayMs;
  const priorStart = now - 2 * days * dayMs;

  let current = 0;
  let prior = 0;
  for (const e of events) {
    if (e.combinationId !== combinationId) continue;
    const t = new Date(e.timestamp).getTime();
    if (t >= currentStart) current++;
    else if (t >= priorStart) prior++;
  }
  if (current > prior) return "up";
  if (current < prior) return "down";
  return "stable";
}

/** Combinations trending within a window, ranked by window execution count. */
export function trending(
  combinations: Combination[],
  events: UsageEvent[],
  window: TrendingWindow,
  n: number,
): CombinationAnalytics[] {
  return combinations
    .map((combination) => ({
      combination,
      windowCount: windowCount(events, combination.id, window),
      trend: trendDirection(events, combination.id, window),
      uniqueUserCount: combination.uniqueUsers.length,
      lastUsedAt: combination.lastUsedAt,
      rank: 0,
    }))
    .filter((c) => c.windowCount > 0)
    .sort((a, b) => b.windowCount - a.windowCount)
    .slice(0, n)
    .map((c, i) => ({ ...c, rank: i + 1 }));
}

/** Full analytics rows for the analytics dashboard, ranked by usage. */
export function analytics(
  combinations: Combination[],
  events: UsageEvent[],
  window: TrendingWindow,
  n: number,
): CombinationAnalytics[] {
  return topByUsage(combinations, n).map((combination, i) => ({
    combination,
    rank: i + 1,
    windowCount: windowCount(events, combination.id, window),
    trend: trendDirection(events, combination.id, window),
    uniqueUserCount: combination.uniqueUsers.length,
    lastUsedAt: combination.lastUsedAt,
  }));
}
